import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ITemporaryLicense } from 'main_app/services/Responses/Auth/ITemporaryLicense';
import { ActionsObservable, combineEpics, createEpicMiddleware, ofType } from 'redux-observable-es6-compat';
import { lastValueFrom, Observable, of as observableOf } from 'rxjs';
import { catchError, map, mergeMap, startWith } from 'rxjs/operators';
import { Account, AuthData, BrixxPlan } from "sharedFeatures/sharedModels";
import { IErrorResponse } from "sharedFeatures/sharedServices/Responses/account.index";
import { IChangePassword } from "sharedFeatures/sharedServices/Responses/Auth/IChangePassword";
import { IChangeSortType } from "sharedFeatures/sharedServices/Responses/Auth/IChangeSortType";
import { ISetLastCompanyId } from "sharedFeatures/sharedServices/Responses/Auth/ISetLastCompanyId";
import { IKeepChoosedPlans } from "sharedFeatures/sharedServices/Responses/BrixxFiles/IKeepChoosedPlans";
import { accountActions, BaseAction } from 'sharedFeatures/store/actions'; // 'sharedFeatures/store' does not work here...
import { DialogCreationService } from "./internalServices/dialogCreation.service";
import { LicenseData } from 'sharedFeatures/sharedModels/AuthData';

declare let handleAngularHttpError: any;

@Injectable()
export class AccountService {
    // Redux epic middleware for http requests
    private epicMiddleware = createEpicMiddleware();

    constructor(private http: HttpClient, private dialogCreation: DialogCreationService, ) {
        //this.CheckIsLoggedOn();
    }

    private AuthData: AuthData = new AuthData();
    private Account: Account = new Account();
    public Plans: Array<BrixxPlan> = [];

    // Middleware is fired by the redux on dispatch action GET_ACCOUNT_DATA
    private getAccountDataMiddleware =
    (action$: ActionsObservable<any>, state$: Account /*ObservableLike with AppState*/): Observable<BaseAction> => {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        return action$.pipe(
            ofType(accountActions.GET_ACCOUNT_DATA),
            mergeMap((action: BaseAction) => {
                return this.http.get<string>(window.appPath + 'Account/AccountSettingsJson', { }).pipe(
                    map((res: string) => {
                        this.Account = JSON.parse(res) as Account;

                        // Path action with account data to the accountReducer
                        return accountActions.accountDataLoadSucceeded(this.Account);
                    }),
                    catchError((error: HttpErrorResponse) => {
                        console.error('AccountSettingsJson error', error);

                        handleAngularHttpError(error);

                        return observableOf(accountActions.accountDataLoadFailed(error));
                    }),
                    startWith(accountActions.accountDataLoadStarted()),);
            }));
    }

    // Middleware is fired by the redux on dispatch action GET_AUTH_DATA
    private getAuthDataMiddleware =
    (action$: ActionsObservable<any>, state$: AuthData /*ObservableLike with AppState*/): Observable<BaseAction> => {
        let me = this;
        let headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('X-XSRF-Token', (<HTMLInputElement>document.getElementsByName('__RequestVerificationToken')[0]).value);

        var fromAccount = window.isFromAccount;

        return action$.pipe(
            ofType(accountActions.GET_AUTH_DATA),
            mergeMap((action: BaseAction) => {
                var nameObj = {
                    doHandleLastPlan: action.payload,
                    isFromAccount: fromAccount
                };

                return this.http.post<string>(window.appPath + 'Auth/CheckIsLoggedIn', nameObj, { headers: headers }).pipe(
                    map((res: string) => {
                        let data = JSON.parse(res) as AuthData;

                        if (!data.success) {
                            const popup = this.dialogCreation.createErrorWarning("Error", "Error receiving 'check logged on' data", () => { popup.close(); });
                            popup.show();
                        }

                        me.AuthData = data;
                        window["isNotifyJSError"] = data.isNotifyJSError;

                        if (data.buildVersion && data.buildVersion !== 'undefined') {
                            window.buildVersion = data.buildVersion;
                        }

                        if (window.isFromAccount) {
                            if (data.isSessionTimeout)
                                window["count"] = 0;
                        }

                        // Path action with auth data to the accountReducer
                        return accountActions.authDataLoadSucceeded(data);
                    }),
                    catchError((error: HttpErrorResponse) => {
                        console.error('GetAuthData error', error);

                        handleAngularHttpError(error);

                        return observableOf(accountActions.authDataLoadFailed(error));
                    }),
                    startWith(accountActions.authDataLoadStarted()),);
            }));
    }

    // Return all middlewares of the account service
    public getMiddlewares() {
        return [
            this.epicMiddleware
        ];
    }
    public runMiddlewares(): void {
        this.epicMiddleware.run(combineEpics(
            this.getAuthDataMiddleware,
            this.getAccountDataMiddleware
        ));
    }    

    // TODO: BR-5133 remove?
    /*CheckNewPlanAvailable() {
        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            this.http.get(window.appPath + 'Auth/CheckNewPlanAvailable', { responseType: 'text' })
                .toPromise().then((res: string) => {
                    let data = JSON.parse(res);
                    this.NewPlanAvailableData = data;

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('CheckNewPlanAvailable error', error);
                    //this.dialogCreation.createUnknownExceptionDialog(error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }*/

    keepChoosedPlans(checkedPlanGUIDs: string) {
        // Wrap http response with the custom promise to handle error
            const response = this.http.post<string>(window.appPath + 'BrixxFiles/KeepChoosedPlans', { planGUIDs: checkedPlanGUIDs });
            return lastValueFrom(response)
                .then((res: string) => {
                    return JSON.parse(res) as IKeepChoosedPlans | IErrorResponse;
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('KeepChoosedPlans error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    throw error;
                });

    }

    changePassword(oldPassword: string, newPassword: string) {
        // Wrap http response with the custom promise to handle error
            const response = this.http.post<string>(window.appPath + 'Auth/ChangePassword', { oldPass: oldPassword, newPass: newPassword });
            return lastValueFrom(response)
                .then((res: string) => {
                    return JSON.parse(res) as IChangePassword | IErrorResponse;
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('ChangePassword error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    throw error;
                });

    }

    changeLastSortOption(sortOption: number, companyId: number) {
        const response = this.http.post<string>(window.appPath + 'Auth/ChangeSortType',
            {
                userName: this.AuthData.email,
                companyId: companyId,
                ChangeSortType: sortOption.toString()
            });
        return lastValueFrom(response)
            .then((res: string) => {
                return JSON.parse(res) as IChangeSortType;
            })
            .catch((error: HttpErrorResponse) => {
                console.error('ChangeSortType error', error);

                handleAngularHttpError(error);

                //if we need to transmit the error to a component
                throw error;
            });

    }

    changeLastCompanyId(companyId: number) {

        // Wrap http response with the custom promise to handle error
        const response = this.http.post<string>(window.appPath + 'Auth/SetLastCompanyId',
            {
                companyId: companyId
            });

        return lastValueFrom(response)
            .then((res: string) => {

                return JSON.parse(res) as ISetLastCompanyId | IErrorResponse;
            })
            .catch((error: HttpErrorResponse) => {
                console.error('changeLastCompanyId error', error);

                handleAngularHttpError(error);

                //if we need to transmit the error to a component
                throw error;
            });
    }

    CreateTemporaryLicense(companyId: number, customerId: number, packageId: string, orderRef: string): Promise<ITemporaryLicense | IErrorResponse>{
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = `${window.appPath}Auth/CreateTemporaryLicense`;

        const data = { companyId: companyId, customerId: customerId, packageId: packageId, orderRef: orderRef }

        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(url, data, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data: ITemporaryLicense | IErrorResponse = { success: false, errorMessage: '' };

                    if (res && res.length) {
                        if (res.indexOf("error=sessiontimeout") === -1)
                            data = JSON.parse(res);
                    }

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('CreateTemporaryLicense error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    getCurrentUserLicense(): Promise<LicenseData> {
        const response = this.http.get<string>(`${window.appPath}Auth/GetCurrentUserLicense`, {});
        return lastValueFrom(response)
            .then((res: string) => {
                let data = JSON.parse(res) as LicenseData;

                if (!data.success) {
                    const popup = this.dialogCreation.createErrorWarning("Error", "Error receiving current user license data", () => { popup.close(); });
                    popup.show();
                }

                return data;
            });
    }

}
